import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import apiResponseHandler from '../../../utils/apiResponseHandler';
import {
  TACapability,
  TARole,
  taRolesApiService,
} from '../../../services/TARolesApiService';

type RouteParams = { roleId: string };
type Props = RouteComponentProps<RouteParams>;

const THEME_ORDER = ['Track', 'Resolve', 'Prove', 'Admin', 'Other'] as const;
type Theme = (typeof THEME_ORDER)[number];

const themeFor = (codename: string): Theme => {
  const code = codename || '';
  if (
    code.startsWith('ta_clock_') ||
    code === 'ta_view_coverage' ||
    code === 'ta_view_posting_sheet' ||
    code.startsWith('ta_bulk_clock_')
  ) {
    return 'Track';
  }
  if (
    code.startsWith('ta_mark_') ||
    code.startsWith('ta_assign_') ||
    code === 'ta_view_exceptions' ||
    code === 'ta_override_clock'
  ) {
    return 'Resolve';
  }
  if (code === 'ta_view_reports' || code === 'ta_generate_reports') {
    return 'Prove';
  }
  if (
    code === 'ta_upload_rosters' ||
    code === 'ta_configure_sites' ||
    code.startsWith('ta_manage_') ||
    code === 'ta_admin_scope'
  ) {
    return 'Admin';
  }
  return 'Other';
};

const TARoleDetail: React.FC<Props> = ({ match }) => {
  const { roleId } = match.params;
  const [role, setRole] = useState<TARole | null>(null);
  const [allCapabilities, setAllCapabilities] = useState<TACapability[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setRole(null);
    setAllCapabilities([]);
    setSelected(new Set());
    setLoading(true);

    Promise.all([
      taRolesApiService.getRole(roleId),
      taRolesApiService.listCapabilities(),
    ])
      .then(([roleData, capabilities]) => {
        if (cancelled) return;
        setRole(roleData);
        setAllCapabilities(capabilities);
        setSelected(new Set((roleData.capabilities || []).map((c) => c.id)));
      })
      .catch((err) => {
        if (cancelled) return;
        apiResponseHandler.handleApiError('Failed to load role', err);
        setRole(null);
        setAllCapabilities([]);
        setSelected(new Set());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [roleId]);

  const toggleCapability = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const grouped = useMemo(() => {
    const byTheme: Record<Theme, TACapability[]> = {
      Track: [],
      Resolve: [],
      Prove: [],
      Admin: [],
      Other: [],
    };
    allCapabilities.forEach((cap) => {
      byTheme[themeFor(cap.codename)].push(cap);
    });
    THEME_ORDER.forEach((theme) => {
      byTheme[theme].sort((a, b) => a.name.localeCompare(b.name));
    });
    return byTheme;
  }, [allCapabilities]);

  const handleSave = () => {
    if (!role) return;
    setSaving(true);
    taRolesApiService
      .updateRoleCapabilities(role.id, Array.from(selected))
      .then((updated) => {
        setRole(updated);
        setSelected(new Set((updated.capabilities || []).map((c) => c.id)));
        apiResponseHandler.handleApiSuccess('Role capabilities saved');
      })
      .catch((err) => {
        apiResponseHandler.handleApiError('Failed to save role', err);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <section className="dashboard">
      <div className="header bg-primary pb-6">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col lg="6">
                <h6 className="h2 text-white d-inline-block mb-0">
                  {role ? role.name : 'T&A Role'}
                </h6>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      <Container fluid className="mt--7">
        <Row>
          <Col xl={{ size: '8', offset: '2' }} lg={{ size: '12' }}>
            <Card className="shadow">
              <CardHeader>
                <Row className="align-items-center">
                  <Col>
                    <h3 className="mb-0">
                      Capabilities{role ? ` — ${role.name}` : ''}
                    </h3>
                  </Col>
                  <Col className="text-right">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleSave}
                      disabled={!role || saving || loading}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="d-flex justify-content-center">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                ) : (
                  THEME_ORDER.map((theme) => {
                    const caps = grouped[theme];
                    if (!caps.length) return null;
                    return (
                      <div key={theme} className="mb-4">
                        <h4>{theme}</h4>
                        <Row>
                          {caps.map((cap) => (
                            <Col md="6" key={cap.id}>
                              <FormGroup check className="mb-2">
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    checked={selected.has(cap.id)}
                                    onChange={() => toggleCapability(cap.id)}
                                    disabled={saving}
                                  />{' '}
                                  {cap.name}
                                  <div>
                                    <small className="text-muted">
                                      {cap.codename}
                                    </small>
                                  </div>
                                </Label>
                              </FormGroup>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    );
                  })
                )}
                {!loading && allCapabilities.length === 0 && (
                  <p className="text-muted mb-0">No capabilities available.</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default TARoleDetail;
